import React, { useState, useMemo } from 'react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Area,
  AreaChart,
} from 'recharts'
import {
  Box,
  Card,
  CardContent,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Chip,
  alpha,
  useTheme,
} from '@mui/material'
import { motion } from 'framer-motion'
import {
  TrendingUp,
  TrendingDown,
  Activity,
  Clock,
  Zap,
} from 'lucide-react'

export default function ModernResponseTimeChart({ logs }) {
  const theme = useTheme()
  const [chartType, setChartType] = useState('line') // 'line' or 'area'
  const [timeRange, setTimeRange] = useState(250) // 50, 100, 250

  // Filter logs based on time range
  const filteredLogs = useMemo(() => {
    return logs.length > timeRange ? logs.slice(-timeRange) : logs
  }, [logs, timeRange])

  // Format data
  const data = useMemo(() => {
    return filteredLogs.map((log) => {
      const d = new Date(log.createdAt)
      const pad = (n) => n.toString().padStart(2, '0')
      const formatted = `${pad(d.getDate())}/${pad(d.getMonth() + 1)} ${pad(
        d.getHours()
      )}:${pad(d.getMinutes())}`
      
      return {
        responseTime: log.responseTime,
        createdAt: formatted,
        fullDate: log.createdAt,
      }
    })
  }, [filteredLogs])

  // Calculate statistics
  const stats = useMemo(() => {
    if (data.length === 0) return null

    const times = data.map((d) => d.responseTime)
    const avg = Math.round(times.reduce((a, b) => a + b, 0) / times.length)
    const min = Math.min(...times)
    const max = Math.max(...times)
    
    // Trend calculation (last 10 vs previous 10)
    const recentAvg = times.slice(-10).reduce((a, b) => a + b, 0) / 10
    const previousAvg = times.slice(-20, -10).reduce((a, b) => a + b, 0) / 10
    const trend = recentAvg > previousAvg ? 'up' : 'down'
    const trendPercent = Math.abs(
      Math.round(((recentAvg - previousAvg) / previousAvg) * 100)
    )

    return { avg, min, max, trend, trendPercent }
  }, [data])

  // Custom Tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null

    return (
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.background.paper,
            0.98
          )}, ${alpha(theme.palette.background.paper, 0.95)})`,
          backdropFilter: 'blur(10px)',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
          borderRadius: 2,
          p: 1.5,
          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
        }}
      >
        <Typography variant="caption" sx={{ color: alpha('#fff', 0.7), display: 'block', mb: 0.5 }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ color: theme.palette.primary.main, fontWeight: 700 }}>
          {payload[0].value} ms
        </Typography>
      </Box>
    )
  }

  // Get X-axis ticks
  const getXAxisTicks = () => {
    if (data.length === 0) return []
    
    const interval = Math.floor(data.length / 5)
    const ticks = []
    
    for (let i = 0; i < 6; i++) {
      const index = Math.min(i * interval, data.length - 1)
      ticks.push(data[index]?.createdAt)
    }
    
    return ticks
  }

  if (!logs || logs.length === 0) {
    return (
      <Card
        sx={{
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.background.paper,
            0.8
          )}, ${alpha(theme.palette.background.paper, 0.6)})`,
          backdropFilter: 'blur(20px)',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          borderRadius: 3,
        }}
      >
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <Activity size={48} color={alpha(theme.palette.primary.main, 0.5)} />
          <Typography variant="h6" sx={{ mt: 2, color: alpha('#fff', 0.7) }}>
            Henüz veri bulunmuyor
          </Typography>
          <Typography variant="body2" sx={{ color: alpha('#fff', 0.5) }}>
            Monitor çalıştığında yanıt süreleri burada görünecek
          </Typography>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card
      component={motion.div}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      sx={{
        background: `linear-gradient(135deg, ${alpha(
          theme.palette.background.paper,
          0.8
        )}, ${alpha(theme.palette.background.paper, 0.6)})`,
        backdropFilter: 'blur(20px)',
        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        borderRadius: 3,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2.5,
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            0.1
          )}, ${alpha(theme.palette.primary.main, 0.05)})`,
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
        >
          <Box>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: 2,
                  background: alpha(theme.palette.primary.main, 0.1),
                }}
              >
                <Activity size={20} color={theme.palette.primary.main} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Yanıt Süreleri
              </Typography>
            </Stack>
            <Typography variant="caption" sx={{ color: alpha('#fff', 0.6) }}>
              Son {data.length} kontrol sonucu
            </Typography>
          </Box>

          {/* Controls */}
          <Stack direction="row" spacing={1.5} flexWrap="wrap">
            {/* Chart Type Toggle */}
            <ToggleButtonGroup
              value={chartType}
              exclusive
              onChange={(e, newType) => newType && setChartType(newType)}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  px: 2,
                  py: 0.5,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                  color: alpha('#fff', 0.7),
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  '&.Mui-selected': {
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                    color: '#000',
                    border: 'none',
                  },
                },
              }}
            >
              <ToggleButton value="line">Line</ToggleButton>
              <ToggleButton value="area">Area</ToggleButton>
            </ToggleButtonGroup>

            {/* Time Range Toggle */}
            <ToggleButtonGroup
              value={timeRange}
              exclusive
              onChange={(e, newRange) => newRange && setTimeRange(newRange)}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  px: 2,
                  py: 0.5,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                  color: alpha('#fff', 0.7),
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  '&.Mui-selected': {
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                    color: '#000',
                    border: 'none',
                  },
                },
              }}
            >
              <ToggleButton value={50}>50</ToggleButton>
              <ToggleButton value={100}>100</ToggleButton>
              <ToggleButton value={250}>250</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Stack>
      </Box>

      {/* Stats */}
      {stats && (
        <Box
          sx={{
            p: 2.5,
            background: alpha(theme.palette.background.default, 0.3),
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <Stack direction="row" spacing={3} flexWrap="wrap">
            {/* Average */}
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Zap size={16} color={theme.palette.primary.main} />
                <Typography variant="caption" sx={{ color: alpha('#fff', 0.6) }}>
                  Ortalama
                </Typography>
              </Stack>
              <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                {stats.avg} ms
              </Typography>
            </Box>

            {/* Min */}
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <TrendingDown size={16} color={theme.palette.success.main} />
                <Typography variant="caption" sx={{ color: alpha('#fff', 0.6) }}>
                  En Düşük
                </Typography>
              </Stack>
              <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
                {stats.min} ms
              </Typography>
            </Box>

            {/* Max */}
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <TrendingUp size={16} color={theme.palette.error.main} />
                <Typography variant="caption" sx={{ color: alpha('#fff', 0.6) }}>
                  En Yüksek
                </Typography>
              </Stack>
              <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.error.main }}>
                {stats.max} ms
              </Typography>
            </Box>

            {/* Trend */}
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Clock size={16} color={theme.palette.info.main} />
                <Typography variant="caption" sx={{ color: alpha('#fff', 0.6) }}>
                  Trend
                </Typography>
              </Stack>
              <Stack direction="row" spacing={0.5} alignItems="center">
                {stats.trend === 'up' ? (
                  <TrendingUp size={20} color={theme.palette.warning.main} />
                ) : (
                  <TrendingDown size={20} color={theme.palette.success.main} />
                )}
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: stats.trend === 'up' ? theme.palette.warning.main : theme.palette.success.main,
                  }}
                >
                  {stats.trendPercent}%
                </Typography>
              </Stack>
            </Box>
          </Stack>
        </Box>
      )}

      {/* Chart */}
      <CardContent sx={{ p: 3 }}>
        <ResponsiveContainer width="100%" height={400}>
          {chartType === 'line' ? (
            <LineChart
              data={data}
              margin={{ top: 10, right: 30, left: 10, bottom: 60 }}
            >
              <defs>
                <linearGradient id="lineGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={theme.palette.primary.main} stopOpacity={1} />
                  <stop offset="100%" stopColor={theme.palette.primary.light} stopOpacity={1} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={alpha(theme.palette.primary.main, 0.1)}
                vertical={false}
              />
              <XAxis
                dataKey="createdAt"
                ticks={getXAxisTicks()}
                tick={{
                  fontSize: 11,
                  fill: alpha('#fff', 0.6),
                }}
                angle={-45}
                textAnchor="end"
                height={80}
                stroke={alpha(theme.palette.primary.main, 0.3)}
              />
              <YAxis
                tick={{
                  fontSize: 12,
                  fill: alpha('#fff', 0.6),
                }}
                label={{
                  value: 'Yanıt Süresi (ms)',
                  angle: -90,
                  position: 'insideLeft',
                  style: {
                    fill: alpha('#fff', 0.7),
                    fontSize: 12,
                    fontWeight: 600,
                  },
                }}
                stroke={alpha(theme.palette.primary.main, 0.3)}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="monotone"
                dataKey="responseTime"
                stroke="url(#lineGradient)"
                strokeWidth={3}
                dot={false}
                activeDot={{
                  r: 6,
                  fill: theme.palette.primary.main,
                  stroke: theme.palette.background.paper,
                  strokeWidth: 2,
                }}
              />
            </LineChart>
          ) : (
            <AreaChart
              data={data}
              margin={{ top: 10, right: 30, left: 10, bottom: 60 }}
            >
              <defs>
                <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={theme.palette.primary.main} stopOpacity={0.8} />
                  <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0.1} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={alpha(theme.palette.primary.main, 0.1)}
                vertical={false}
              />
              <XAxis
                dataKey="createdAt"
                ticks={getXAxisTicks()}
                tick={{
                  fontSize: 11,
                  fill: alpha('#fff', 0.6),
                }}
                angle={-45}
                textAnchor="end"
                height={80}
                stroke={alpha(theme.palette.primary.main, 0.3)}
              />
              <YAxis
                tick={{
                  fontSize: 12,
                  fill: alpha('#fff', 0.6),
                }}
                label={{
                  value: 'Yanıt Süresi (ms)',
                  angle: -90,
                  position: 'insideLeft',
                  style: {
                    fill: alpha('#fff', 0.7),
                    fontSize: 12,
                    fontWeight: 600,
                  },
                }}
                stroke={alpha(theme.palette.primary.main, 0.3)}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="responseTime"
                stroke={theme.palette.primary.main}
                strokeWidth={3}
                fill="url(#areaGradient)"
                dot={false}
                activeDot={{
                  r: 6,
                  fill: theme.palette.primary.main,
                  stroke: theme.palette.background.paper,
                  strokeWidth: 2,
                }}
              />
            </AreaChart>
          )}
        </ResponsiveContainer>
      </CardContent>
    </Card>
  )
}